import { providerQueries } from './queries';
import { providerMutations } from './mutations';
import { supabase } from '@/lib/supabase';
export { ProviderServiceError } from './errors';

export const providerService = {
  ...providerQueries,
  ...providerMutations
};

// Create devices service
export const deviceService = {
  async getDevices() {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async getDeviceById(id: number) {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async createDevice(device: { name: string; category: string }) {
    const { data, error } = await supabase
      .from('devices')
      .insert(device)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateDevice(id: number, updates: { name?: string; category?: string }) {
    const { data, error } = await supabase
      .from('devices')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteDevice(id: number) {
    const { error } = await supabase
      .from('devices')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Enhanced Streaming Specifications Service with Template + Override Pattern
export const streamingSpecsService = {
  /**
   * Get provider template for streaming specifications
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param regionId - Optional region ID for regional filtering
   */
  async getProviderTemplate(providerId: number, versionId: number, regionId?: number) {
    console.log('Getting provider template for:', { providerId, versionId, regionId });
    
    const { data, error } = await supabase
      .rpc('get_streaming_specs_template' as any, {
        p_provider_id: providerId,
        p_version_id: versionId,
        p_region_id: regionId
      });
    
    if (error) {
      console.error('RPC function error:', error);
      throw error;
    }
    
    console.log('RPC function returned:', data);
    return (data as any)?.[0] || null;
  },

  /**
   * Get resolved streaming specifications (template + overrides)
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param regionId - Optional region ID for regional filtering
   * @param planId - Optional plan ID to get specific plan data
   */
  async getResolvedSpecs(providerId: number, versionId: number, regionId?: number, planId?: number) {
    const { data, error } = await supabase
      .rpc('get_streaming_specs_resolved' as any, {
        p_provider_id: providerId,
        p_version_id: versionId,
        p_region_id: regionId,
        p_plan_id: planId
      });
    
    if (error) throw error;
    return (data as any) || [];
  },

  /**
   * Get streaming specifications for a provider with optional regional filtering (legacy method)
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param regionId - Optional region ID for regional filtering
   */
  async getStreamingSpecs(providerId: number, versionId: number, regionId?: number) {
    let query = supabase
      .from('streaming_specifications')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    // Apply regional filtering with entity scoping logic
    if (regionId !== undefined) {
      // Provider-level specs apply globally, plan/category specs are regional
      query = query.or(`region_id.eq.${regionId},entity_type.eq.provider`);
    }

    const { data, error } = await query.order('entity_type');
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get streaming specifications by entity with regional architecture support
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param entityType - Entity type (provider applies globally, plan/category regionally)
   * @param entityId - Entity ID (null for provider-level)
   * @param regionId - Region ID (required for plan/category entities)
   */
  async getStreamingSpecsByEntity(
    providerId: number, 
    versionId: number, 
    entityType: 'provider' | 'plan' | 'category', 
    entityId?: number,
    regionId?: number
  ) {
    let query = supabase
      .from('streaming_specifications')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('entity_type', entityType);

    // Apply entity scoping logic based on regional architecture
    if (entityType === 'provider') {
      // Provider-level specs apply globally (entity_id is null)
      query = query.is('entity_id', null);
      // Region can be specified for provider-specific regional specs, or null for global
      if (regionId !== undefined) {
        query = query.eq('region_id', regionId);
      }
    } else {
      // Plan/category specs are regional and require both entity_id and region_id
      if (entityId) {
        query = query.eq('entity_id', entityId);
      }
      if (regionId !== undefined) {
        query = query.eq('region_id', regionId);
      }
    }

    const { data, error } = await query.single();
    
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "not found"
    return data;
  },

  /**
   * Create provider template for streaming specifications
   */
  async createProviderTemplate(specs: {
    provider_id: number;
    version_id: number;
    region_id?: number | null;
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    max_video_quality?: string | null;
    download_limit_days?: number | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
  }) {
    const templateSpecs = {
      ...specs,
      entity_type: 'provider' as const,
      entity_id: null
    };

    const { data, error } = await supabase
      .from('streaming_specifications')
      .insert(templateSpecs)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Update provider template for streaming specifications
   */
  async updateProviderTemplate(providerId: number, versionId: number, updates: {
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    max_video_quality?: string | null;
    download_limit_days?: number | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
  }, regionId?: number) {
    let query = supabase
      .from('streaming_specifications')
      .update(updates)
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null);

    if (regionId !== undefined) {
      query = query.eq('region_id', regionId);
    } else {
      query = query.is('region_id', null);
    }

    const { data, error } = await query.select().single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Upsert provider template for streaming specifications
   */
  async upsertProviderTemplate(specs: {
    provider_id: number;
    version_id: number;
    region_id?: number | null;
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    max_video_quality?: string | null;
    download_limit_days?: number | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
  }) {
    const templateSpecs = {
      ...specs,
      entity_type: 'provider' as const,
      entity_id: null
    };

    console.log('Upserting provider template with specs:', templateSpecs);

    // Use standardized upsert pattern with unique constraint
    const { data, error } = await supabase
      .from('streaming_specifications')
      .upsert(templateSpecs, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) {
      console.error('Upsert error:', error);
      throw error;
    }
    
    console.log('Successfully upserted provider template:', data);
    return data;
  },

  /**
   * Create or update plan override for streaming specifications
   */
  async upsertPlanOverride(planId: number, overrides: {
    max_video_quality?: string | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    download_limit_days?: number | null;
  }) {
    const overrideData = {
      plan_id: planId,
      max_video_quality: overrides.max_video_quality,
      hdr_support: overrides.hdr_support,
      dolby_atmos_support: overrides.dolby_atmos_support,
      simultaneous_streams: overrides.simultaneous_streams,
      offline_downloads: overrides.offline_downloads,
      download_limit_days: overrides.download_limit_days
    };

    const { data, error } = await (supabase as any)
      .from('streaming_specifications_overrides')
      .upsert(overrideData, {
        onConflict: 'plan_id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get plan overrides for streaming specifications
   */
  async getPlanOverrides(planIds: number[]) {
    const { data, error } = await (supabase as any)
      .from('streaming_specifications_overrides')
      .select('*')
      .in('plan_id', planIds);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Delete plan override for streaming specifications
   */
  async deletePlanOverride(planId: number) {
    const { error } = await (supabase as any)
      .from('streaming_specifications_overrides')
      .delete()
      .eq('plan_id', planId);
    
    if (error) throw error;
  },

  /**
   * Create streaming specifications with regional support (legacy method)
   */
  async createStreamingSpecs(specs: {
    provider_id: number;
    version_id: number;
    region_id?: number | null;
    entity_type: 'provider' | 'plan' | 'category';
    entity_id?: number | null;
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    max_video_quality?: string | null;
    download_limit_days?: number | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
  }) {
    // Validate regional architecture constraints
    if (specs.entity_type === 'provider') {
      // Provider-level specs apply globally, so entity_id should be null
      specs.entity_id = null;
      // Region can be null for global provider specs, or set for regional provider-specific specs
    } else if (specs.entity_type === 'plan' || specs.entity_type === 'category') {
      // Plan/category specs require region_id for regional scoping
      if (specs.region_id === undefined || specs.region_id === null) {
        throw new Error(`Region ID is required for ${specs.entity_type}-level streaming specifications`);
      }
    }

    const { data, error } = await supabase
      .from('streaming_specifications')
      .insert(specs)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Update streaming specifications
   */
  async updateStreamingSpecs(id: number, updates: {
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    max_video_quality?: string | null;
    download_limit_days?: number | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
  }) {
    const { data, error } = await supabase
      .from('streaming_specifications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Delete streaming specifications
   */
  async deleteStreamingSpecs(id: number) {
    const { error } = await supabase
      .from('streaming_specifications')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Get streaming specifications by provider with entity inheritance
   * Provides a consolidated view where plan/category specs inherit from provider specs
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param regionId - Region ID for regional filtering
   */
  async getStreamingSpecsWithInheritance(providerId: number, versionId: number, regionId?: number) {
    // Get all specs for the provider/version
    const specs = await this.getStreamingSpecs(providerId, versionId, regionId);
    
    // Find provider-level specs (global baseline)
    const providerSpecs = specs.find(spec => 
      spec.entity_type === 'provider' && 
      spec.entity_id === null
    );
    
    // Group plan/category specs by entity
    const entitySpecs = specs.filter(spec => spec.entity_type !== 'provider');
    
    return {
      providerSpecs,
      entitySpecs,
      allSpecs: specs
    };
  },

  /**
   * Upsert streaming specifications (create or update if exists)
   * Handles the unique constraint including region_id
   */
  async upsertStreamingSpecs(specs: {
    provider_id: number;
    version_id: number;
    region_id?: number | null;
    entity_type: 'provider' | 'plan' | 'category';
    entity_id?: number | null;
    simultaneous_streams?: number | null;
    offline_downloads?: boolean | null;
    max_video_quality?: string | null;
    download_limit_days?: number | null;
    hdr_support?: boolean | null;
    dolby_atmos_support?: boolean | null;
  }) {
    // Apply the same validation as createStreamingSpecs
    if (specs.entity_type === 'provider') {
      specs.entity_id = null;
    } else if (specs.entity_type === 'plan' || specs.entity_type === 'category') {
      if (specs.region_id === undefined || specs.region_id === null) {
        throw new Error(`Region ID is required for ${specs.entity_type}-level streaming specifications`);
      }
    }

    const { data, error } = await supabase
      .from('streaming_specifications')
      .upsert(specs, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

// Enhanced Additional Fees Service with Template + Override Pattern
export const additionalFeesService = {
  /**
   * Get provider template for additional fees
   * Enhanced to return results ordered by display_order
   */
  async getProviderTemplate(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('additional_fees')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .order('display_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get resolved additional fees (template + overrides)
   */
  async getResolvedFees(providerId: number, versionId: number, regionId: number, planId?: number) {
    const { data, error } = await supabase
      .rpc('get_additional_fees_resolved' as any, {
        p_provider_id: providerId,
        p_version_id: versionId,
        p_region_id: regionId,
        p_plan_id: planId
      });
    
    if (error) throw error;
    return (data as any) || [];
  },

  /**
   * Upsert provider template for additional fees
   * Enhanced to support multiple same fee types and custom "others" option
   */
  async upsertProviderTemplate(fee: {
    provider_id: number;
    version_id: number;
    region_id: number;
    fee_type: string;
    custom_fee_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    amount: number;
    description?: string | null;
    is_recurring?: boolean | null;
    is_required?: boolean | null;
  }) {
    // If display_order not provided, get next available order
    let displayOrder = fee.display_order || 1;
    if (!fee.display_order) {
      const { data: maxOrder } = await supabase
        .from('additional_fees')
        .select('display_order')
        .eq('provider_id', fee.provider_id)
        .eq('version_id', fee.version_id)
        .eq('region_id', fee.region_id)
        .eq('entity_type', 'provider')
        .is('entity_id', null)
        .order('display_order', { ascending: false })
        .limit(1);
      
      displayOrder = (maxOrder && maxOrder[0] && (maxOrder[0] as any).display_order) ? 
        ((maxOrder[0] as any).display_order + 1) : 1;
    }

    const templateFee = {
      ...fee,
      entity_type: 'provider' as const,
      entity_id: null,
      custom_fee_type_name: fee.custom_fee_type_name,
      display_order: displayOrder,
      internal_reference: fee.internal_reference
    };

    const { data, error } = await supabase
      .from('additional_fees')
      .upsert(templateFee, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id,display_order',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Create provider template for additional fees (legacy method)
   */
  async createProviderTemplate(fee: {
    provider_id: number;
    version_id: number;
    region_id: number;
    fee_type: string;
    amount: number;
    description?: string | null;
    is_recurring?: boolean;
    is_required?: boolean;
  }) {
    const templateFee = {
      ...fee,
      entity_type: 'provider' as const,
      entity_id: null
    };

    const { data, error } = await supabase
      .from('additional_fees')
      .insert(templateFee)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Update provider template for additional fees
   */
  async updateProviderTemplate(feeId: number, updates: {
    amount?: number;
    description?: string | null;
    is_recurring?: boolean;
    is_required?: boolean;
  }) {
    const { data, error } = await supabase
      .from('additional_fees')
      .update(updates)
      .eq('id', feeId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get plan overrides for additional fees
   */
  async getPlanOverrides(planIds: number[]) {
    const { data, error } = await (supabase as any)
      .from('additional_fees_overrides')
      .select('*')
      .in('plan_id', planIds);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Create or update plan override for additional fees
   * Enhanced to support multiple same fee types and custom "others" option
   */
  async upsertPlanOverride(planId: number, overrides: {
    fee_type?: string;
    custom_fee_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    amount?: number | null;
    description?: string | null;
    is_recurring?: boolean | null;
    is_required?: boolean | null;
  }) {
    // If display_order not provided, get next available order for this plan
    let displayOrder = overrides.display_order || 1;
    if (!overrides.display_order) {
      const { data: maxOrder } = await (supabase as any)
        .from('additional_fees_overrides')
        .select('display_order')
        .eq('plan_id', planId)
        .order('display_order', { ascending: false })
        .limit(1);
      
      displayOrder = (maxOrder && maxOrder[0] && maxOrder[0].display_order) ? 
        (maxOrder[0].display_order + 1) : 1;
    }

    const { data, error } = await (supabase as any)
      .from('additional_fees_overrides')
      .upsert({
        plan_id: planId,
        fee_type: overrides.fee_type,
        custom_fee_type_name: overrides.custom_fee_type_name,
        display_order: displayOrder,
        internal_reference: overrides.internal_reference,
        amount: overrides.amount,
        description: overrides.description,
        is_recurring: overrides.is_recurring,
        is_required: overrides.is_required
      }, {
        onConflict: 'plan_id,display_order'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Delete plan override for additional fees
   */
  async deletePlanOverride(planId: number, feeType?: string) {
    let query = (supabase as any)
      .from('additional_fees_overrides')
      .delete()
      .eq('plan_id', planId);

    if (feeType) {
      query = query.eq('fee_type', feeType);
    }

    const { error } = await query;
    if (error) throw error;
  },

  /**
   * Get all fees for a provider
   * Enhanced to return results ordered by display_order
   */
  async getAllFees(providerId: string, regionId: string, versionId: string) {
    const { data, error } = await supabase
      .from('additional_fees')
      .select('*')
      .eq('provider_id', parseInt(providerId))
      .eq('region_id', parseInt(regionId))
      .eq('version_id', parseInt(versionId))
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .order('display_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new fee
   */
  async createFee(fee: {
    provider_id: number;
    version_id: number;
    region_id: number;
    entity_type: 'provider' | 'plan' | 'category';
    entity_id?: number | null;
    fee_type: string;
    custom_fee_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    amount: number;
    description?: string | null;
    is_recurring?: boolean;
    is_required?: boolean;
  }) {
    const { data, error } = await supabase
      .from('additional_fees')
      .insert(fee)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Upsert a fee (insert if not exists, update if exists)
   */
  async upsertFee(fee: {
    provider_id: number;
    version_id: number;
    region_id: number;
    entity_type: 'provider' | 'plan' | 'category';
    entity_id?: number | null;
    fee_type: string;
    custom_fee_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    amount: number;
    description?: string | null;
    is_recurring?: boolean;
    is_required?: boolean;
  }) {
    const { data, error } = await supabase
      .from('additional_fees')
      .upsert(fee, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Update an existing fee
   */
  async updateFee(id: number, updates: {
    fee_type?: string;
    custom_fee_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    amount?: number;
    description?: string | null;
    is_recurring?: boolean;
    is_required?: boolean;
  }) {
    const { data, error } = await supabase
      .from('additional_fees')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Delete a fee
   */
  async deleteFee(id: number) {
    const { error } = await supabase
      .from('additional_fees')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Get next available display order for a provider template
   */
  async getNextDisplayOrder(providerId: number, versionId: number, regionId: number) {
    const { data } = await supabase
      .from('additional_fees')
      .select('display_order')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .order('display_order', { ascending: false })
      .limit(1);
    
    return (data && data[0] && (data[0] as any).display_order) ? 
      ((data[0] as any).display_order + 1) : 1;
  },

  /**
   * Update display order of fees (for reordering)
   */
  async updateDisplayOrder(feeId: number, newOrder: number) {
    const { data, error } = await supabase
      .from('additional_fees')
      .update({ display_order: newOrder } as any)
      .eq('id', feeId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get fees with "others" fee type and their custom names
   */
  async getCustomFees(providerId: number, regionId: number, versionId: number) {
    const { data, error } = await supabase
      .from('additional_fees')
      .select('*')
      .eq('provider_id', providerId)
      .eq('region_id', regionId)
      .eq('version_id', versionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .eq('fee_type', 'others')
      .order('display_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  }
};

// Enhanced Promotional Offers Service with Template + Override Pattern
export const promotionalOffersService = {
  /**
   * Get provider template for promotional offers
   * Enhanced to return results ordered by display_order
   */
  async getProviderTemplate(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .order('display_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Upsert provider template for promotional offers
   * Enhanced to support multiple same discount types and custom "others" option
   */
  async upsertProviderTemplate(offer: {
    provider_id: number;
    version_id: number;
    region_id: number;
    discount_type?: string;
    custom_discount_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    title: string;
    description?: string | null;
    original_price?: number | null;
    promotional_price?: number | null;
    discount_percentage?: number | null;
    eligibility_requirements?: string | null;
    is_recurring?: boolean | null;
    status: string;
    starts_at?: string | null;
    ends_at?: string | null;
    is_new_customer_only?: boolean | null;
    terms_conditions?: string | null;
  }) {
    // If display_order not provided, get next available order
    let displayOrder = offer.display_order || 1;
    if (!offer.display_order) {
      const { data: maxOrder } = await supabase
        .from('promotional_offers')
        .select('display_order')
        .eq('provider_id', offer.provider_id)
        .eq('version_id', offer.version_id)
        .eq('region_id', offer.region_id)
        .eq('entity_type', 'provider')
        .is('entity_id', null)
        .order('display_order', { ascending: false })
        .limit(1);
      
      displayOrder = (maxOrder && maxOrder[0] && (maxOrder[0] as any).display_order) ? 
        ((maxOrder[0] as any).display_order + 1) : 1;
    }

    const templateOffer = {
      ...offer,
      entity_type: 'provider' as const,
      entity_id: null,
      discount_type: offer.discount_type || 'general',
      custom_discount_type_name: offer.custom_discount_type_name,
      display_order: displayOrder,
      internal_reference: offer.internal_reference,
      status: offer.status || 'time_limited'
    };

    const { data, error } = await supabase
      .from('promotional_offers')
      .upsert(templateOffer, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id,display_order'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get plan overrides for promotional offers
   */
  async getPlanOverrides(planIds: number[]) {
    const { data, error } = await (supabase as any)
      .from('promotional_offers_overrides')
      .select('*')
      .in('plan_id', planIds);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Upsert plan override for promotional offers
   * Enhanced to support multiple same discount types and custom "others" option
   */
  async upsertPlanOverride(planId: number, overrides: {
    title?: string | null;
    description?: string | null;
    original_price?: number | null;
    promotional_price?: number | null;
    discount_percentage?: number | null;
    discount_type?: string | null;
    custom_discount_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    eligibility_requirements?: string | null;
    is_recurring?: boolean | null;
    status?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    is_new_customer_only?: boolean | null;
    terms_conditions?: string | null;
  }) {
    // If display_order not provided, get next available order for this plan
    let displayOrder = overrides.display_order || 1;
    if (!overrides.display_order) {
      const { data: maxOrder } = await (supabase as any)
        .from('promotional_offers_overrides')
        .select('display_order')
        .eq('plan_id', planId)
        .order('display_order', { ascending: false })
        .limit(1);
      
      displayOrder = (maxOrder && maxOrder[0] && maxOrder[0].display_order) ? 
        (maxOrder[0].display_order + 1) : 1;
    }

    const overrideData = {
      plan_id: planId,
      title: overrides.title,
      description: overrides.description,
      original_price: overrides.original_price,
      promotional_price: overrides.promotional_price,
      discount_percentage: overrides.discount_percentage,
      discount_type: overrides.discount_type || 'general',
      custom_discount_type_name: overrides.custom_discount_type_name,
      display_order: displayOrder,
      internal_reference: overrides.internal_reference,
      eligibility_requirements: overrides.eligibility_requirements,
      is_recurring: overrides.is_recurring,
      status: overrides.status,
      starts_at: overrides.starts_at,
      ends_at: overrides.ends_at,
      is_new_customer_only: overrides.is_new_customer_only,
      terms_conditions: overrides.terms_conditions
    };

    const { data, error } = await (supabase as any)
      .from('promotional_offers_overrides')
      .upsert(overrideData, {
        onConflict: 'plan_id,display_order'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
  /**
   * Get active promotional offers for a provider
   * @param providerId - Provider ID
   * @param regionId - Optional region ID for regional filtering
   * @param versionId - Optional version ID for version-specific offers
   */
  async getActiveOffers(providerId: number, regionId?: number, versionId?: number) {
    const now = new Date().toISOString();
    let query = supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .lte('starts_at', now)
      .gte('ends_at', now);

    // Apply regional filtering for region-scoped entities
    if (regionId !== undefined) {
      query = query.or(`region_id.eq.${regionId},entity_type.eq.provider`);
    }

    // Apply version filtering if provided
    if (versionId !== undefined) {
      query = query.eq('version_id', versionId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get all promotional offers for a provider (active and inactive)
   * Enhanced to return results ordered by display_order
   * @param providerId - Provider ID
   * @param regionId - Optional region ID for regional filtering
   * @param versionId - Optional version ID for version-specific offers
   */
  async getAllOffers(providerId: number, regionId?: number, versionId?: number) {
    let query = supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId);

    // Apply regional filtering - provider-level offers apply globally
    if (regionId !== undefined) {
      query = query.or(`region_id.eq.${regionId},entity_type.eq.provider`);
    }

    // Apply version filtering if provided
    if (versionId !== undefined) {
      query = query.eq('version_id', versionId);
    }

    const { data, error } = await query.order('display_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get offers by entity type with regional architecture support
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param entityType - Entity type (provider applies globally, plan/category regionally)
   * @param entityId - Entity ID (null for provider-level)
   * @param regionId - Region ID (required for plan/category entities)
   */
  async getOffersByEntity(
    providerId: number, 
    versionId: number,
    entityType: 'provider' | 'plan' | 'category', 
    entityId?: number,
    regionId?: number
  ) {
    const now = new Date().toISOString();
    let query = supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('entity_type', entityType)
      .lte('starts_at', now)
      .gte('ends_at', now);

    // Apply entity scoping logic
    if (entityType === 'provider') {
      // Provider-level offers apply globally (entity_id is null)
      query = query.is('entity_id', null);
    } else {
      // Plan/category offers are regional and require both entity_id and region_id
      if (entityId) {
        query = query.eq('entity_id', entityId);
      }
      if (regionId !== undefined) {
        query = query.eq('region_id', regionId);
      }
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new promotional offer with regional support
   * Enhanced to support custom "others" discount type
   */
  async createOffer(offer: {
    provider_id: number;
    version_id: number;
    region_id?: number | null;
    entity_type: 'provider' | 'plan' | 'category';
    entity_id?: number | null;
    title: string;
    description?: string | null;
    original_price?: number | null;
    promotional_price?: number | null;
    discount_percentage?: number | null;
    discount_type?: 'general' | 'student' | 'senior' | 'military' | 'bundle' | 'others';
    custom_discount_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    eligibility_requirements?: string | null;
    is_recurring?: boolean;
    status?: 'ongoing' | 'time_limited';
    starts_at?: string;
    ends_at?: string;
    is_new_customer_only?: boolean;
    terms_conditions?: string | null;
  }) {
    // Validate regional architecture constraints
    if (offer.entity_type === 'provider') {
      // Provider-level offers apply globally, so entity_id should be null
      offer.entity_id = null;
      // Region can be null for global provider offers, or set for regional provider-specific offers
    } else if (offer.entity_type === 'plan' || offer.entity_type === 'category') {
      // Plan/category offers require region_id for regional scoping
      if (offer.region_id === undefined || offer.region_id === null) {
        throw new Error(`Region ID is required for ${offer.entity_type}-level promotional offers`);
      }
    }

    const { data, error } = await supabase
      .from('promotional_offers')
      .insert({
        provider_id: offer.provider_id,
        version_id: offer.version_id,
        region_id: offer.region_id,
        entity_type: offer.entity_type,
        entity_id: offer.entity_id,
        title: offer.title,
        description: offer.description,
        original_price: offer.original_price,
        promotional_price: offer.promotional_price,
        discount_percentage: offer.discount_percentage,
        discount_type: offer.discount_type || 'general',
        custom_discount_type_name: offer.custom_discount_type_name,
        display_order: offer.display_order,
        internal_reference: offer.internal_reference,
        eligibility_requirements: offer.eligibility_requirements,
        is_recurring: offer.is_recurring || false,
        status: offer.status || 'time_limited',
        starts_at: offer.starts_at,
        ends_at: offer.ends_at,
        is_new_customer_only: offer.is_new_customer_only || false,
        terms_conditions: offer.terms_conditions
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Update an existing promotional offer
   */
  async updateOffer(id: number, updates: {
    title?: string;
    description?: string | null;
    original_price?: number | null;
    promotional_price?: number | null;
    discount_percentage?: number | null;
    discount_type?: 'general' | 'student' | 'senior' | 'military' | 'bundle' | 'others';
    custom_discount_type_name?: string | null;
    display_order?: number;
    internal_reference?: string | null;
    eligibility_requirements?: string | null;
    is_recurring?: boolean;
    status?: 'ongoing' | 'time_limited';
    starts_at?: string;
    ends_at?: string;
    is_new_customer_only?: boolean;
    terms_conditions?: string | null;
  }) {
    const { data, error } = await supabase
      .from('promotional_offers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Delete a promotional offer
   */
  async deleteOffer(id: number) {
    const { error } = await supabase
      .from('promotional_offers')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Get offers by discount type (new feature)
   */
  async getOffersByDiscountType(
    providerId: number,
    discountType: 'general' | 'student' | 'senior' | 'military' | 'bundle' | 'others',
    regionId?: number,
    versionId?: number
  ) {
    const now = new Date().toISOString();
    let query = supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .eq('discount_type', discountType)
      .lte('starts_at', now)
      .gte('ends_at', now);

    // Apply regional filtering
    if (regionId !== undefined) {
      query = query.or(`region_id.eq.${regionId},entity_type.eq.provider`);
    }

    // Apply version filtering if provided
    if (versionId !== undefined) {
      query = query.eq('version_id', versionId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get recurring promotional offers
   */
  async getRecurringOffers(providerId: number, regionId?: number, versionId?: number) {
    const now = new Date().toISOString();
    let query = supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .eq('is_recurring', true)
      .lte('starts_at', now)
      .gte('ends_at', now);

    // Apply regional filtering
    if (regionId !== undefined) {
      query = query.or(`region_id.eq.${regionId},entity_type.eq.provider`);
    }

    // Apply version filtering if provided
    if (versionId !== undefined) {
      query = query.eq('version_id', versionId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get next available display order for a provider template
   */
  async getNextDisplayOrder(providerId: number, versionId: number, regionId: number) {
    const { data } = await supabase
      .from('promotional_offers')
      .select('display_order')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .order('display_order', { ascending: false })
      .limit(1);
    
    return (data && data[0] && (data[0] as any).display_order) ? 
      ((data[0] as any).display_order + 1) : 1;
  },


  /**
   * Update display order of offers (for reordering)
   */
  async updateDisplayOrder(offerId: number, newOrder: number) {
    const { data, error } = await supabase
      .from('promotional_offers')
      .update({ display_order: newOrder } as any)
      .eq('id', offerId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get offers with "others" discount type and their custom names
   */
  async getCustomOffers(providerId: number, regionId?: number, versionId?: number) {
    let query = supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .eq('discount_type', 'others');

    if (regionId !== undefined) {
      query = query.eq('region_id', regionId);
    }

    if (versionId !== undefined) {
      query = query.eq('version_id', versionId);
    }

    const { data, error } = await query.order('display_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  }
};;
// Enhanced Provider Device Support Service with Regional Architecture Support
export const providerDeviceService = {
  /**
   * Get all available devices
   */
  async getAllDevices() {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  },
  /**
   * Get provider device support with optional regional filtering
   * Includes all entity-specific device support (provider/plan/category-specific)
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param regionId - Optional region ID for regional filtering
   */
  async getProviderDeviceSupport(providerId: number, versionId: number, regionId?: number) {
    let query = supabase
      .from('provider_device_support')
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    // Apply regional filtering if provided
    if (regionId !== undefined) {
      // Get records for the specific region or global records (region_id IS NULL)
      query = query.or(`region_id.eq.${regionId},region_id.is.null`);
    }

    const { data, error } = await query.order('entity_type', { ascending: true }).order('device(name)');
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get device support by device ID with regional context
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param deviceId - Device ID
   * @param regionId - Optional region ID for regional filtering
   */
  async getDeviceSupport(providerId: number, versionId: number, deviceId: number, regionId?: number) {
    let query = supabase
      .from('provider_device_support')
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('device_id', deviceId);

    if (regionId !== undefined) {
      query = query.eq('region_id', regionId);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Update device support with regional architecture support
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param deviceId - Device ID
   * @param regionId - Region ID (required for new records)
   * @param support - Support configuration including entity-specific parameters
   */
  async updateDeviceSupport(
    providerId: number, 
    versionId: number, 
    deviceId: number, 
    regionId: number,
    support: {
      is_supported: boolean;
      notes?: string | null;
      entity_type?: 'provider' | 'plan' | 'category';
      entity_id?: number | null;
    }
  ) {
    const { data, error } = await supabase
      .from('provider_device_support')
      .upsert({
        provider_id: providerId,
        version_id: versionId,
        device_id: deviceId,
        region_id: regionId,
        is_supported: support.is_supported,
        notes: support.notes,
        entity_type: support.entity_type || 'provider',
        entity_id: support.entity_id || null
      }, {
        onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id'
      })
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `)
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Update device support with backward compatibility (regionId optional)
   * Falls back to updating all regions if regionId not provided
   * Now supports entity-specific device support (plan/category-specific)
   */
  async updateDeviceSupportLegacy(
    providerId: number, 
    versionId: number, 
    deviceId: number, 
    support: {
      is_supported: boolean;
      notes?: string | null;
      entity_type?: 'provider' | 'plan' | 'category';
      entity_id?: number | null;
    },
    regionId?: number
  ) {
    if (regionId !== undefined) {
      // Use the new regional method
      return this.updateDeviceSupport(providerId, versionId, deviceId, regionId, support);
    }

    // Legacy behavior: update all existing records for this provider/version/device
    const existingRecords = await this.getDeviceSupport(providerId, versionId, deviceId);
    
    if (existingRecords.length === 0) {
      throw new Error('Region ID is required for creating new device support records. Existing records not found.');
    }

    // Update all existing regional records
    const updates = existingRecords.map(record => ({
      provider_id: providerId,
      version_id: versionId,
      device_id: deviceId,
      region_id: record.region_id,
      is_supported: support.is_supported,
      notes: support.notes,
      entity_type: support.entity_type || 'provider',
      entity_id: support.entity_id || null
    }));

    const { data, error } = await supabase
      .from('provider_device_support')
      .upsert(updates, {
        onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id'
      })
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Remove device support with regional context
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param deviceId - Device ID
   * @param regionId - Optional region ID. If not provided, removes all regional records
   */
  async removeDeviceSupport(providerId: number, versionId: number, deviceId: number, regionId?: number) {
    let query = supabase
      .from('provider_device_support')
      .delete()
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('device_id', deviceId);

    if (regionId !== undefined) {
      query = query.eq('region_id', regionId);
    } else {
      // If no region is specified, we should probably throw an error
      // or clarify that this will delete from ALL regions, which could be dangerous.
      // For now, let's require a regionId to be safe.
      throw new Error("Region ID is required to remove device support records.");
    }

    const { error } = await query;
    
    if (error) throw error;
  },

  /**
   * Bulk update device support with regional architecture support
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param regionId - Region ID (required for consistency)
   * @param devices - Array of device support configurations
   */
  async bulkUpdateDeviceSupport(
    providerId: number, 
    versionId: number, 
    regionId: number,
    devices: Array<{
      device_id: number;
      is_supported: boolean;
      notes?: string | null;
    }>
  ) {
    const supportEntries = devices.map(device => ({
      provider_id: providerId,
      version_id: versionId,
      region_id: regionId,
      device_id: device.device_id,
      is_supported: device.is_supported,
      notes: device.notes || null
    }));

    const { data, error } = await supabase
      .from('provider_device_support')
      .upsert(supportEntries, {
        onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id'
      })
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Upsert device support (for provider templates)
   */
  async upsertDeviceSupport(supportData: {
    provider_id: number;
    version_id: number;
    region_id: number | null;
    device_id: number;
    is_supported: boolean;
    notes?: string | null;
    entity_type: string;
    entity_id: number | null;
  }) {
    const { data, error } = await supabase
      .from('provider_device_support')
      .upsert(supportData, {
        onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get plan overrides for device support
   */
  async getPlanOverrides(planIds: number[]) {
    const { data, error } = await (supabase as any)
      .from('provider_device_support_overrides')
      .select('*')
      .in('plan_id', planIds);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Upsert plan override for device support
   */
  async upsertPlanOverride(planId: number, deviceId: number, overrides: {
    is_supported: boolean;
    notes?: string | null;
  }) {
    const overrideData = {
      plan_id: planId,
      device_id: deviceId,
      is_supported: overrides.is_supported,
      notes: overrides.notes
    };

    const { data, error } = await (supabase as any)
      .from('provider_device_support_overrides')
      .upsert(overrideData, {
        onConflict: 'plan_id,device_id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Bulk update device support with backward compatibility
   * Falls back to legacy behavior if regionId not provided
   */
  async bulkUpdateDeviceSupportLegacy(
    providerId: number, 
    versionId: number, 
    devices: Array<{
      device_id: number;
      is_supported: boolean;
      notes?: string | null;
    }>,
    regionId?: number
  ) {
    if (regionId !== undefined) {
      // Use the new regional method
      return this.bulkUpdateDeviceSupport(providerId, versionId, regionId, devices);
    }

    // Legacy behavior: find existing region contexts and update them
    const existingSupport = await this.getProviderDeviceSupport(providerId, versionId);
    const existingRegions = [...new Set(existingSupport.map(s => s.region_id))].filter(Boolean);
    
    if (existingRegions.length === 0) {
      throw new Error('Region ID is required for bulk updates when no existing regional records found.');
    }

    // Update for each existing region
    const allUpdates = [];
    for (const currentRegionId of existingRegions) {
      const supportEntries = devices.map(device => ({
        provider_id: providerId,
        version_id: versionId,
        region_id: currentRegionId,
        device_id: device.device_id,
        is_supported: device.is_supported,
        notes: device.notes || null
      }));
      allUpdates.push(...supportEntries);
    }

    const { data, error } = await supabase
      .from('provider_device_support')
      .upsert(allUpdates, {
        onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id'
      })
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Get device support grouped by region
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param deviceId - Optional device ID to filter by specific device
   */
  async getDeviceSupportByRegion(providerId: number, versionId: number, deviceId?: number) {
    let query = supabase
      .from('provider_device_support')
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (deviceId !== undefined) {
      query = query.eq('device_id', deviceId);
    }

    const { data, error } = await query.order('region(name)');
    
    if (error) throw error;
    
    // Group by region
    const groupedByRegion = (data || []).reduce((acc, item) => {
      const regionKey = item.region?.id || 'unknown';
      if (!acc[regionKey]) {
        acc[regionKey] = {
          region: item.region,
          devices: []
        };
      }
      acc[regionKey].devices.push(item);
      return acc;
    }, {} as Record<string, { region: any; devices: any[] }>);

    return groupedByRegion;
  },

  /**
   * Get supported devices for a provider across all regions
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param supportedOnly - Filter to only supported devices
   */
  async getSupportedDevices(providerId: number, versionId: number, supportedOnly: boolean = true) {
    let query = supabase
      .from('provider_device_support')
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (supportedOnly) {
      query = query.eq('is_supported', true);
    }

    const { data, error } = await query.order('device(name)');
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Copy device support from one region to another
   * @param providerId - Provider ID
   * @param versionId - Version ID
   * @param sourceRegionId - Source region ID
   * @param targetRegionId - Target region ID
   * @param overwriteExisting - Whether to overwrite existing records in target region
   */
  async copyDeviceSupportBetweenRegions(
    providerId: number,
    versionId: number,
    sourceRegionId: number,
    targetRegionId: number,
    overwriteExisting: boolean = false
  ) {
    // Get source region device support
    const sourceData = await this.getProviderDeviceSupport(providerId, versionId, sourceRegionId);
    
    if (sourceData.length === 0) {
      throw new Error(`No device support data found for source region ${sourceRegionId}`);
    }

    // Prepare target region data
    const targetEntries = sourceData.map(item => ({
      provider_id: providerId,
      version_id: versionId,
      region_id: targetRegionId,
      device_id: item.device_id,
      is_supported: item.is_supported,
      notes: item.notes
    }));

    const upsertConfig = overwriteExisting ? 
      { onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id' } : 
      { onConflict: 'provider_id,device_id,version_id,region_id,entity_type,entity_id', ignoreDuplicates: true };

    const { data, error } = await supabase
      .from('provider_device_support')
      .upsert(targetEntries, upsertConfig)
      .select(`
        *,
        device:devices(*),
        region:regions(id, name)
      `);
    
    if (error) throw error;
    return data || [];
  }
};



// Content Coverage Service
export const contentCoverageService = {
  /**
   * Get provider template for content coverage
   */
  async getProviderTemplate(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('content_coverage')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Get content coverage for a provider region (legacy method)
   */
  async getContentCoverage(providerId: number, versionId: number, regionId: number) {
    return this.getProviderTemplate(providerId, versionId, regionId);
  },

  /**
   * Upsert provider template for content coverage
   */
  async upsertProviderTemplate(coverage: {
    provider_id: number;
    version_id: number;
    region_id: number;
    sports_coverage?: 'extensive' | 'basic' | 'none';
    news_coverage?: '24_7' | 'basic' | 'none';
    international_content?: boolean;
    kids_family_content?: boolean;
    premium_movies?: boolean;
    local_channels?: boolean;
    original_content?: boolean;
  }) {
    const { data, error } = await supabase
      .from('content_coverage')
      .upsert(coverage, {
        onConflict: 'provider_id,version_id,region_id'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Upsert content coverage (legacy method)
   */
  async upsertContentCoverage(coverage: {
    provider_id: number;
    version_id: number;
    region_id: number;
    sports_coverage?: 'extensive' | 'basic' | 'none';
    news_coverage?: '24_7' | 'basic' | 'none';
    international_content?: boolean;
    kids_family_content?: boolean;
    premium_movies?: boolean;
    local_channels?: boolean;
    original_content?: boolean;
  }) {
    return this.upsertProviderTemplate(coverage);
  },

  /**
   * Delete content coverage
   */
  async deleteContentCoverage(id: number) {
    const { error } = await supabase
      .from('content_coverage')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};;;

// Service Terms Service with Template + Override Pattern
export const serviceTermsService = {
  /**
   * Get provider template for service terms
   */
  async getProviderTemplate(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('service_terms')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .eq('entity_type', 'provider')
      .is('entity_id', null)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Upsert provider template for service terms
   */
  async upsertProviderTemplate(terms: {
    provider_id: number;
    version_id: number;
    region_id: number;
    contract_required?: string | null;
    cancellation_policy?: string | null;
    installation_required?: boolean | null;
    price_lock_period_months?: number | null;
  }) {
    const templateTerms = {
      ...terms,
      entity_type: 'provider' as const,
      entity_id: null
    };
    
    // Remove id field if it exists to avoid conflicts
    const { id, ...upsertData } = templateTerms as any;

    const { data, error } = await supabase
      .from('service_terms')
      .upsert(upsertData, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get plan overrides for service terms
   */
  async getPlanOverrides(planIds: number[]) {
    const { data, error } = await (supabase as any)
      .from('service_terms_overrides')
      .select('*')
      .in('plan_id', planIds);
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Upsert plan override for service terms
   */
  async upsertPlanOverride(planId: number, overrides: {
    contract_required?: string | null;
    cancellation_policy?: string | null;
    installation_required?: boolean | null;
    price_lock_period_months?: number | null;
  }) {
    const overrideData = {
      plan_id: planId,
      contract_required: overrides.contract_required,
      cancellation_policy: overrides.cancellation_policy,
      installation_required: overrides.installation_required,
      price_lock_period_months: overrides.price_lock_period_months
    };

    const { data, error } = await (supabase as any)
      .from('service_terms_overrides')
      .upsert(overrideData, {
        onConflict: 'plan_id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Get service terms for a provider region (legacy method)
   */
  async getServiceTerms(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('service_terms')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Upsert service terms (legacy method)
   */
  async upsertServiceTerms(terms: {
    provider_id: number;
    version_id: number;
    region_id: number;
    contract_required?: 'none' | '1_year' | '2_year' | 'flexible';
    cancellation_policy?: 'anytime' | '30_days' | 'fee_required';
    installation_required?: boolean;
    price_lock_period_months?: number | null;
  }) {
    // Remove id field if it exists to avoid conflicts
    const { id, ...upsertData } = terms as any;
    
    const { data, error } = await supabase
      .from('service_terms')
      .upsert(upsertData, {
        onConflict: 'provider_id,version_id,region_id,entity_type,entity_id'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Delete service terms
   */
  async deleteServiceTerms(id: number) {
    const { error } = await supabase
      .from('service_terms')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Service Quality Service
export const serviceQualityService = {
  /**
   * Get service quality for a provider region
   */
  async getServiceQuality(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('service_quality')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Upsert service quality
   */
  async upsertServiceQuality(quality: {
    provider_id: number;
    version_id: number;
    region_id: number;
    customer_support_hours?: '24_7' | 'business_hours' | 'limited';
    support_channels?: string[];
    setup_difficulty?: 'self_install' | 'professional' | 'mixed';
    reliability_rating?: number;
    customer_satisfaction_score?: number;
  }) {
    // Remove id field if it exists to avoid conflicts
    const { id, ...upsertData } = quality as any;
    
    const { data, error } = await supabase
      .from('service_quality')
      .upsert(upsertData, {
        onConflict: 'provider_id,version_id,region_id'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  /**
   * Delete service quality
   */
  async deleteServiceQuality(id: number) {
    const { error } = await supabase
      .from('service_quality')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};


